import { memo } from "react";
import { Box } from "@mui/material";
import StarFilled from "@ant-design/icons/StarFilled";
import StarOutlined from "@ant-design/icons/StarOutlined";

/**
 * Lightweight, read-only star rating that visually matches MUI <Rating readOnly>
 * with Ant Design StarFilled/StarOutlined icons, but without MUI Rating's
 * per-decimal-item machinery (which renders max/precision RatingItems — 50 at
 * precision 0.1 — each cloning both icons).
 *
 * It renders exactly `max` empty (outline) stars plus `max` filled (gold) stars
 * in an absolutely-positioned overlay clipped to the fractional fill width, so a
 * value like 3.7 reads identically to MUI at precision 0.1 — at ~2*max icons
 * instead of ~2*(max/precision).
 *
 * Colors mirror what MUI <Rating> renders here: filled #faaf00 (Rating's
 * hardcoded default), empty = theme palette.action.disabled (this app overrides
 * it to grey[300] = #d9d9d9 in light mode), referenced via the sx color token so
 * it tracks the theme/mode exactly like MUI Rating's iconEmpty does.
 *
 * @param {number}        value      - rating value (0..max)
 * @param {number|string} fontSize   - icon font-size (passed straight to the icons; e.g. 10 or "55%")
 * @param {number}        [max=5]
 * @param {number}        [precision=0.1] - fill is quantized to this step to match MUI precision
 */
function StaticStarRating({ value, fontSize, max = 5, precision = 0.1 }) {
    const v = Math.min(max, Math.max(0, Number(value) || 0));
    const quantized = Math.round(v / precision) * precision;
    const pct = (quantized / max) * 100;

    const filled = Array.from({ length: max }, (_, i) => <StarFilled key={i} style={{ fontSize }} />);
    const empty = Array.from({ length: max }, (_, i) => <StarOutlined key={i} style={{ fontSize }} />);

    return (
        <Box
            role="img"
            aria-label={`${quantized} out of ${max}`}
            sx={{ position: "relative", display: "inline-flex", lineHeight: 0, verticalAlign: "middle" }}
        >
            <Box aria-hidden="true" sx={{ display: "flex", color: "action.disabled" }}>
                {empty}
            </Box>
            <Box
                aria-hidden="true"
                sx={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${pct}%`, overflow: "hidden", color: "#faaf00" }}
            >
                <Box sx={{ display: "flex", width: "max-content" }}>
                    {filled}
                </Box>
            </Box>
        </Box>
    );
}

export default memo(StaticStarRating);
